import isNil from 'lodash/isNil';

import {
  CalendarDate,
  isAfterOrEqual,
  isBefore,
  setCalendarDate,
  subtractCalendarDate,
  toCalendarDate,
  toCalendarDateFromParts,
} from './dateutils';
import { EnumMessagesEN } from './i18n/enumMessages';
import { ADVANCED_INDEXATION_LEASE_TYPES } from './leaseutils';
import { IndexType, LeaseType, UnitType } from './types/model-app';

const FIXED_BASE_INDEX_SIGNATURE_LIMIT = '1981-01-01';
const CONSUMER_PRICE_START_LIMIT = '1984-01-01';
const CONSUMER_PRICE_SIGNATURE_LIMIT = '1994-01-01';
const REGIONAL_RULES_START = '2019-01-01';
const FLANDERS_CORRECTION_THRESHOLD_MONTH = 9;
const BRUSSELS_CORRECTION_THRESHOLD_MONTH = 10;
const BRUSSELS_CORRECTION_THRESHOLD_DAY = 14;
const CORRECTION_FACTOR_OLDEST_YEAR = 2022;
const CORRECTION_FACTOR_NEWEST_YEAR = 2023;

/** Index series a starting index is read from, or why it is not read from the Statbel file. */
export enum StartingIndexSource {
  HEALTH = 'HEALTH',
  CONSUMER_PRICE = 'CONSUMER_PRICE',
  CONSUMER_PRICE_NO_ENERGY = 'CONSUMER_PRICE_NO_ENERGY',
  FIXED_BEFORE_1981 = 'FIXED_BEFORE_1981',
  UNAVAILABLE = 'UNAVAILABLE',
}

const SOURCE_BY_INDEX_TYPE: Record<IndexType, StartingIndexSource> = {
  [IndexType.HEALTH]: StartingIndexSource.HEALTH,
  [IndexType.CONSUMER_PRICE]: StartingIndexSource.CONSUMER_PRICE,
  [IndexType.CONSUMER_PRICE_NO_ENERGY]: StartingIndexSource.CONSUMER_PRICE_NO_ENERGY,
};

export interface StartingIndexReference {
  month: CalendarDate | null;
  source: StartingIndexSource;
}

export interface StartingIndexParams {
  signatureDate: CalendarDate;
  startDate: CalendarDate;
  region: string;
  baseIndexDate?: CalendarDate | null;
  indexType?: IndexType | null;
}

export interface CustomIndexationParams {
  leaseType?: LeaseType | null;
  unitType?: UnitType | null;
  advancedIndexation?: boolean | null;
  indexType?: IndexType | null;
  baseYear?: number | null;
}

interface ViableCustomIndexation {
  indexType: IndexType;
  baseYear: number;
}

export interface CorrectionFactorIndexMonths {
  oldest: CalendarDate;
  newest: CalendarDate;
}

const oneMonthBefore = (date: CalendarDate): CalendarDate => subtractCalendarDate(date, 1, 'months');

/** Month of the new index: the month before the lease anniversary reached at the indexation process date. */
export const getNewIndexMonth = (leaseStartDate: CalendarDate, indexationProcessDate: CalendarDate): CalendarDate => {
  const referenceDate = oneMonthBefore(leaseStartDate);
  const processDate = oneMonthBefore(indexationProcessDate);
  const yearToUse = processDate.month < referenceDate.month ? processDate.year - 1 : processDate.year;
  return toCalendarDateFromParts(yearToUse, referenceDate.month, 1);
};

const CUSTOM_INDEXATION_UNIT_TYPES: UnitType[] = [UnitType.WAREHOUSE, UnitType.OFFICE, UnitType.COMMERCIAL];

/**
 * A common law lease trusts its lease type; other leases need a warehouse, office or commercial unit. A base year of 0
 * is no base year.
 */
export const isViableCustomIndexation = (
  customIndexation: CustomIndexationParams
): customIndexation is CustomIndexationParams & ViableCustomIndexation => {
  const { leaseType, unitType, advancedIndexation, indexType, baseYear } = customIndexation;
  return (
    !isNil(leaseType) &&
    (leaseType === LeaseType.COMMON_LAW || (!isNil(unitType) && CUSTOM_INDEXATION_UNIT_TYPES.includes(unitType))) &&
    ADVANCED_INDEXATION_LEASE_TYPES.includes(leaseType) &&
    Boolean(advancedIndexation) &&
    !isNil(indexType) &&
    !isNil(baseYear) &&
    baseYear !== 0
  );
};

/** Statbel rent calculator rules for the starting index (custom indexation uses the lease base index date). */
export const getStartingIndexReference = ({
  signatureDate,
  startDate,
  region,
  baseIndexDate,
  indexType,
}: StartingIndexParams): StartingIndexReference => {
  const oneMonthBeforeSignature = oneMonthBefore(signatureDate);
  if (!isNil(indexType)) {
    return { month: baseIndexDate ?? oneMonthBeforeSignature, source: SOURCE_BY_INDEX_TYPE[indexType] };
  }
  if (isBefore(startDate, toCalendarDate(CONSUMER_PRICE_START_LIMIT))) {
    if (isBefore(signatureDate, toCalendarDate(FIXED_BASE_INDEX_SIGNATURE_LIMIT))) {
      return { month: null, source: StartingIndexSource.FIXED_BEFORE_1981 };
    }
    return { month: oneMonthBefore(startDate), source: StartingIndexSource.CONSUMER_PRICE };
  }
  if (
    isAfterOrEqual(startDate, toCalendarDate(REGIONAL_RULES_START)) &&
    region === EnumMessagesEN.AddressRegion.Flanders
  ) {
    return { month: oneMonthBefore(startDate), source: StartingIndexSource.HEALTH };
  }
  if (isAfterOrEqual(signatureDate, toCalendarDate(REGIONAL_RULES_START)) && region === '') {
    return { month: null, source: StartingIndexSource.UNAVAILABLE };
  }
  if (isBefore(signatureDate, toCalendarDate(CONSUMER_PRICE_SIGNATURE_LIMIT))) {
    return { month: oneMonthBeforeSignature, source: StartingIndexSource.CONSUMER_PRICE };
  }
  return { month: oneMonthBeforeSignature, source: StartingIndexSource.HEALTH };
};

/** Brussels and Flanders compare the anniversaries on either side of their 2022-2023 reduction window. */
export const isAfterCorrectionFactorThreshold = (region: string, date: CalendarDate): boolean => {
  if (region === EnumMessagesEN.AddressRegion.Flanders) {
    return date.month > FLANDERS_CORRECTION_THRESHOLD_MONTH;
  }
  return (
    date.month > BRUSSELS_CORRECTION_THRESHOLD_MONTH ||
    (date.month === BRUSSELS_CORRECTION_THRESHOLD_MONTH && date.day >= BRUSSELS_CORRECTION_THRESHOLD_DAY)
  );
};

/** Months of the two indexes an EPB correction factor divides (oldest ÷ newest). */
export const getCorrectionFactorIndexMonths = (
  region: string,
  leaseStartDate: CalendarDate,
  indexationProcessDate: CalendarDate
): CorrectionFactorIndexMonths | null => {
  switch (region) {
    case EnumMessagesEN.AddressRegion.Wallonia:
      return {
        oldest: oneMonthBefore(setCalendarDate(leaseStartDate, CORRECTION_FACTOR_OLDEST_YEAR, 'year')),
        newest: oneMonthBefore(setCalendarDate(leaseStartDate, CORRECTION_FACTOR_NEWEST_YEAR, 'year')),
      };
    case EnumMessagesEN.AddressRegion.Flanders:
    case EnumMessagesEN.AddressRegion.Brussels: {
      const newestYear = isAfterCorrectionFactorThreshold(region, indexationProcessDate)
        ? CORRECTION_FACTOR_OLDEST_YEAR
        : CORRECTION_FACTOR_NEWEST_YEAR;
      return {
        oldest: getNewIndexMonth(leaseStartDate, setCalendarDate(indexationProcessDate, newestYear - 1, 'year')),
        newest: getNewIndexMonth(leaseStartDate, setCalendarDate(indexationProcessDate, newestYear, 'year')),
      };
    }
    default:
      return null;
  }
};
